import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { ExecutiveMattersResponse, ExecutivePulseResponse, ExecutiveReportResponse } from '../../shared/api-types.js';
import { DISCUSSION_CLIMATES, DISCUSSION_CLIMATE_LABELS } from '../../shared/enums.js';
import { api } from '../api.js';
import { PATHS } from '../paths.js';
import { useExecutiveBadge } from '../shell/executive-badge.js';
import { LinkButton, Section, StatTile, StatTileRow, StateView, useNow } from '../ui/index.js';
import { ExecutiveFailure } from './executive-parts.js';
import { PULSE_TILE_MIN } from './executive-pulse.js';
import { CLIMATE_NOT_COUNTED_TITLE, NO_REPORT_TITLE, climateNotCountedSentence, noReportDescription, openMattersText, previousWeekHint, thinSentence, writtenFromSentence } from './lib/executive.js';
import { useRequest } from '../ui/use-request.js';

// The Overview card for executives (ADR 0019; design §2.13: "headline, four climate StatTiles with basis, 'N open
// matters', 'Open the executive report' — the A9 precedent GeneralIntelligenceCard … stays as the daily door"; the stage 6
// brief: "The Overview card stays"). Shown only to a person the layer is offered to (selection; the routes enforce).
//
// Its three reads are those of what it shows, made when the Overview is shown and never on another page: the latest
// report (its headline — reading it records this executive's first open of that report on the server, as the Report
// section's read does, so the shell's badge is told), the pulse's counts for the four tiles and their basis, and the open
// shared matters for their count. Each is audited by the server as that read always is — the first open of a report, and
// at most one row per executive, view and UTC day for the pulse and the shared list (ADR 0019, "Audit each read").

export function ExecutiveCard() {
  const report = useRequest<ExecutiveReportResponse>((signal) => api.get<ExecutiveReportResponse>('/api/executive/report', { signal }), []);
  const pulse = useRequest<ExecutivePulseResponse>((signal) => api.get<ExecutivePulseResponse>('/api/executive/pulse?limit=1', { signal }), []);
  const matters = useRequest<ExecutiveMattersResponse>((signal) => api.get<ExecutiveMattersResponse>('/api/executive/matters?status=open', { signal }), []);
  const { markOpened } = useExecutiveBadge();
  const now = useNow();
  const latest = report.data?.report ?? null;
  useEffect(() => {
    if (latest) markOpened({ id: latest.id, windowEnd: latest.windowEnd });
  }, [latest, markOpened]);

  const body = (() => {
    if (report.status === 'loading' && !report.data) return <StateView kind="loading" label="Loading the organisation report" minHeight="6rem" compact />;
    if (report.status === 'error' && report.error && !report.data) return <ExecutiveFailure failure={report.error} what="the organisation report" onRetry={report.reload} />;
    if (!report.data) return null;
    // Why the wait and its time (the owner's ruling of 2026-10-09 on ADR 0019's Open 44, "First report's time shown"; lib/executive.ts
    // `noReportDescription`): the report's route carries the first report's wait, so the card states it as Coverage does —
    // never the route's next due instant, which with no report yet is the moment of the read.
    if (!latest) return <p className="secondary" data-executive-card-empty="">{`${NO_REPORT_TITLE}. ${noReportDescription(report.data.running !== null, report.data.firstReport, now)}`}</p>;
    // Why by the reason its executor recorded (the owner's ruling of 2026-10-13 on ADR 0019's Open 50, "Record the reasons";
    // lib/executive.ts `thinSentence`): the card states the cause as the record does.
    if (latest.status === 'insufficient' && latest.insufficient) return <p className="executive-headline">{thinSentence(latest.insufficient)}</p>;
    return (
      <div className="stack stack--2">
        {latest.headline ? <p className="executive-headline">{latest.headline}</p> : null}
        <p className="summary-provenance">{writtenFromSentence(latest)}</p>
      </div>
    );
  })();

  const tiles = (() => {
    const data = pulse.data;
    if (!data) return null;
    if (data.meetings.state === 'counted') {
      const { today, previousWeek } = data.meetings;
      return (
        <StatTileRow minWidth={PULSE_TILE_MIN} className="executive-tiles">
          {DISCUSSION_CLIMATES.map((climate) => (
            <StatTile key={climate} label={DISCUSSION_CLIMATE_LABELS[climate]} value={today[climate]} hint={previousWeekHint(previousWeek ? previousWeek[climate] : null)} to={PATHS.executivePulse} />
          ))}
        </StatTileRow>
      );
    }
    // No minimum since the owner's ruling of 2026-10-12: a report with no climate count says so, never "not enough" — and
    // why, where it recorded why (his ruling of 2026-10-13 on ADR 0019's Open 54, "Record the reasons"), else the rule.
    if (data.meetings.state === 'not_counted') return <p className="text-sm secondary">{`${CLIMATE_NOT_COUNTED_TITLE}. ${climateNotCountedSentence(data.meetings.reason)}`}</p>;
    return null;
  })();

  return (
    <Section
      id="executive-intelligence"
      title="Executive intelligence"
      actions={<LinkButton to={PATHS.executive} variant="link" size="sm" trailingIcon="chevron-right">Open the executive report</LinkButton>}
    >
      <div className="stack stack--3 executive-card">
        {body}
        {tiles}
        {matters.data ? <p className="text-sm"><Link to={PATHS.executiveMatters} className="hit-area">{openMattersText(matters.data.items.length)}</Link></p> : null}
      </div>
    </Section>
  );
}
